import { lazy, Suspense, type ComponentType, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useSession } from "../auth/sessionContext";
import { AppShell } from "../components/AppShell/AppShell";
import { CommerceRouteGuard } from "../commerce/CommerceRouteGuard";
import { CatalogReplyActivityRouteGuard } from "../commerce/CatalogReplyActivityRouteGuard";
import { CommerceTrackingRouteGuard } from "../commerce/CommerceTrackingRouteGuard";
import { roleCanAccessPath, workspaceHomePath } from "../navigation/navigation";
import { MigrationPlaceholderPage } from "../pages/MigrationPlaceholderPage";
import { RevenueRouteGuard } from "../revenue/RevenueRouteGuard";
import { SimpleBillingRouteGuard } from "../simple-billing/SimpleBillingRouteGuard";
import { WorkflowRouteGuard } from "../workflows/WorkflowRouteGuard";
import { WorkflowProjectsRouteGuard } from "../workflows/WorkflowProjectsRouteGuard";

function lazyNamed(
  importer: () => Promise<Record<string, unknown>>,
  exportName: string,
) {
  return lazy(async () => {
    const module = await importer();
    const component = module[exportName];
    if (typeof component !== "function") {
      throw new Error(`Lazy route export ${exportName} is not a component`);
    }
    return { default: component as ComponentType };
  });
}

const DashboardPage = lazyNamed(() => import("../dashboard/DashboardPage"), "DashboardPage");
const CommerceAdminFoundation = lazyNamed(() => import("../commerce/CommerceAdminFoundation"), "CommerceAdminFoundation");
const StorefrontAdminPage = lazyNamed(() => import("../commerce/StorefrontAdminPage"), "StorefrontAdminPage");
const CategoriesAdminPage = lazyNamed(() => import("../commerce/CategoriesAdminPage"), "CategoriesAdminPage");
const CatalogueAdminPage = lazyNamed(() => import("../commerce/CatalogueAdminPage"), "CatalogueAdminPage");
const WebsiteCatalogImportPage = lazyNamed(() => import("../commerce/WebsiteCatalogImportPage"), "WebsiteCatalogImportPage");
const CatalogReplyActivityPage = lazyNamed(() => import("../commerce/CatalogReplyActivityPage"), "CatalogReplyActivityPage");
const CatalogueItemEditorPage = lazyNamed(() => import("../commerce/CatalogueItemEditorPage"), "CatalogueItemEditorPage");
const BuyingPricingEditorPage = lazyNamed(() => import("../commerce/BuyingPricingEditorPage"), "BuyingPricingEditorPage");
const CommerceShippingSettingsPage = lazyNamed(() => import("../commerce/CommerceShippingSettingsPage"), "CommerceShippingSettingsPage");
const CommerceSettingsPage = lazyNamed(() => import("../commerce/CommerceSettingsPage"), "CommerceSettingsPage");
const TrackingLinksPage = lazyNamed(() => import("../commerce/TrackingLinksPage"), "TrackingLinksPage");
const CommerceOrdersPage = lazyNamed(() => import("../commerce/CommerceOrdersPage"), "CommerceOrdersPage");
const CommerceOrderDetailPage = lazyNamed(() => import("../commerce/CommerceOrderDetailPage"), "CommerceOrderDetailPage");
const FollowupsPage = lazyNamed(() => import("../followups/FollowupsPage"), "FollowupsPage");
const CallingQueuePage = lazyNamed(() => import("../gtm/CallingQueuePage"), "CallingQueuePage");
const DiscoverBusinessesPage = lazyNamed(() => import("../gtm/DiscoverBusinessesPage"), "DiscoverBusinessesPage");
const GTMDashboardPage = lazyNamed(() => import("../gtm/GTMDashboardPage"), "GTMDashboardPage");
const GTMAnalyticsPage = lazyNamed(() => import("../gtm/GTMAnalyticsPage"), "GTMAnalyticsPage");
const GTMLayout = lazyNamed(() => import("../gtm/GTMLayout"), "GTMLayout");
const GTMTodayPage = lazyNamed(() => import("../gtm/GTMTodayPage"), "GTMTodayPage");
const ProspectFormPage = lazyNamed(() => import("../gtm/ProspectFormPage"), "ProspectFormPage");
const ProspectsPage = lazyNamed(() => import("../gtm/ProspectsPage"), "ProspectsPage");
const IntegrationsPage = lazyNamed(() => import("../integrations/IntegrationsPage"), "IntegrationsPage");
const LeadsPage = lazyNamed(() => import("../leads/LeadsPage"), "LeadsPage");
const LeadDetailPage = lazyNamed(() => import("../lead-detail/LeadDetailPage"), "LeadDetailPage");
const NewLeadPage = lazyNamed(() => import("../new-lead/NewLeadPage"), "NewLeadPage");
const ReportsPage = lazyNamed(() => import("../reports/ReportsPage"), "ReportsPage");
const RevenuePage = lazyNamed(() => import("../revenue/RevenuePage"), "RevenuePage");
const QuotationEditorPage = lazyNamed(() => import("../quotations/QuotationEditorPage"), "QuotationEditorPage");
const QuotationPage = lazyNamed(() => import("../quotations/QuotationPage"), "QuotationPage");
const BillingSettingsPage = lazyNamed(() => import("../simple-billing/BillingSettingsPage"), "BillingSettingsPage");
const BillDetailsPage = lazyNamed(() => import("../simple-billing/BillDetailsPage"), "BillDetailsPage");
const NewBillPage = lazyNamed(() => import("../simple-billing/NewBillPage"), "NewBillPage");
const ComparativeEstimatesPage = lazyNamed(() => import("../simple-billing/ComparativeEstimatesPage"), "ComparativeEstimatesPage");
const PreviousBillsPage = lazyNamed(() => import("../simple-billing/PreviousBillsPage"), "PreviousBillsPage");
const PendingPaymentsPage = lazyNamed(() => import("../simple-billing/PendingPaymentsPage"), "PendingPaymentsPage");
const CollectionsSummaryPage = lazyNamed(() => import("../simple-billing/CollectionsSummaryPage"), "CollectionsSummaryPage");
const BusinessSummaryPage = lazyNamed(() => import("../simple-billing/BusinessSummaryPage"), "BusinessSummaryPage");
const ExpensesPage = lazyNamed(() => import("../simple-billing/ExpensesPage"), "ExpensesPage");
const ProductsRatesPage = lazyNamed(() => import("../simple-billing/ProductsRatesPage"), "ProductsRatesPage");
const SalesSummaryPage = lazyNamed(() => import("../simple-billing/SalesSummaryPage"), "SalesSummaryPage");
const InventoryPage = lazyNamed(() => import("../simple-billing/InventoryPage"), "InventoryPage");
const BillingInventoryAccessPage = lazyNamed(() => import("../simple-billing/BillingInventoryAccessPage"), "BillingInventoryAccessPage");
const SimpleBillingHomePage = lazyNamed(() => import("../simple-billing/SimpleBillingHomePage"), "SimpleBillingHomePage");
const SimpleBillingShell = lazyNamed(() => import("../simple-billing/SimpleBillingShell"), "SimpleBillingShell");
const SubscriptionPage = lazyNamed(() => import("../subscription/SubscriptionPage"), "SubscriptionPage");
const BillingSubscriptionPage = lazyNamed(() => import("../subscription/BillingSubscriptionPage"), "BillingSubscriptionPage");
const TeamSetupPage = lazyNamed(() => import("../team-setup/TeamSetupPage"), "TeamSetupPage");
const BillingTeamPage = lazyNamed(() => import("../team-setup/BillingTeamPage"), "BillingTeamPage");
const WorkspaceSetupPage = lazyNamed(() => import("../team-setup/WorkspaceSetupPage"), "WorkspaceSetupPage");
const WorkflowEditorPage = lazyNamed(() => import("../workflows/WorkflowEditorPage"), "WorkflowEditorPage");
const WorkflowsPage = lazyNamed(() => import("../workflows/WorkflowsPage"), "WorkflowsPage");
const PhotographyProjectPage = lazyNamed(() => import("../workflows/PhotographyProjectPage"), "PhotographyProjectPage");
const PhotographyProjectsPage = lazyNamed(() => import("../workflows/PhotographyProjectsPage"), "PhotographyProjectsPage");
const WorkflowOperationsPage = lazyNamed(() => import("../workflows/WorkflowOperationsPage"), "WorkflowOperationsPage");
const WorkflowReportsPage = lazyNamed(() => import("../workflows/WorkflowReportsPage"), "WorkflowReportsPage");
const WorkflowLaunchPage = lazyNamed(() => import("../workflows/WorkflowLaunchPage"), "WorkflowLaunchPage");

function RouteLoading() {
  return <div className="app-loading" role="status" aria-label="Loading page" />;
}

function AuthorizedScreen({ screen, children }: { screen: string; children?: ReactNode }) {
  const { user, workspaceProduct, features } = useSession();
  const location = useLocation();
  if (!roleCanAccessPath(user.role, location.pathname, workspaceProduct)) {
    return <Navigate to={workspaceHomePath(user.role, workspaceProduct, features)} replace />;
  }
  return children || <MigrationPlaceholderPage screen={screen} />;
}

export function AppRoutes() {
  const { user, tenant, features, featureTenantId, workspaceProduct, crmAccess } = useSession();
  const homePath = workspaceHomePath(user.role, workspaceProduct, features);
  const billingWorkspace = workspaceProduct === "SIMPLE_BILLING" && user.role !== "super_admin";
  const crmActivationRequired = workspaceProduct === "CRM"
    && user.role !== "super_admin"
    && crmAccess?.state === "unactivated";
  if (crmActivationRequired) {
    if (user.role === "staff") {
      return (
        <Suspense fallback={<RouteLoading />}>
          <Routes>
            <Route element={<AppShell user={user} tenant={tenant} features={[]} featureTenantId={featureTenantId} workspaceProduct={workspaceProduct} crmAccess={crmAccess} />}>
              <Route
                path="*"
                element={(
                  <section className="rt-empty">
                    <strong>CRM access is awaiting activation</strong>
                    <span>Ask your workspace owner to complete the paid pilot or subscription. Your account and workspace data remain safe.</span>
                  </section>
                )}
              />
            </Route>
          </Routes>
        </Suspense>
      );
    }
    return (
      <Suspense fallback={<RouteLoading />}>
        <Routes>
          <Route element={<AppShell user={user} tenant={tenant} features={[]} featureTenantId={featureTenantId} workspaceProduct={workspaceProduct} crmAccess={crmAccess} />}>
            <Route index element={<Navigate to="/setup" replace />} />
            <Route path="setup" element={<AuthorizedScreen screen="Workspace Setup"><WorkspaceSetupPage /></AuthorizedScreen>} />
            <Route path="subscription" element={<AuthorizedScreen screen="Subscription"><SubscriptionPage /></AuthorizedScreen>} />
            <Route path="*" element={<Navigate to="/setup" replace />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route element={<AppShell user={user} tenant={tenant} features={features} featureTenantId={featureTenantId} workspaceProduct={workspaceProduct} crmAccess={crmAccess} />}>
        <Route index element={<Navigate to={homePath} replace />} />
        <Route path="dashboard" element={billingWorkspace ? <Navigate to={homePath} replace /> : <DashboardPage />} />
        <Route path="leads" element={<AuthorizedScreen screen="Leads"><LeadsPage /></AuthorizedScreen>} />
        <Route path="leads/:leadId" element={<AuthorizedScreen screen="Lead Detail"><LeadDetailPage /></AuthorizedScreen>} />
        <Route path="new-lead" element={<AuthorizedScreen screen="New Lead"><NewLeadPage /></AuthorizedScreen>} />
        <Route path="followups" element={<AuthorizedScreen screen="Follow-ups"><FollowupsPage /></AuthorizedScreen>} />
        <Route path="gtm" element={<AuthorizedScreen screen="GTM"><GTMLayout /></AuthorizedScreen>}>
          <Route index element={<GTMDashboardPage />} />
          <Route path="today" element={<GTMTodayPage />} />
          <Route path="analytics" element={<GTMAnalyticsPage />} />
          <Route path="prospects" element={<ProspectsPage />} />
          <Route path="prospects/new" element={<ProspectFormPage />} />
          <Route path="prospects/:prospectId" element={<ProspectFormPage />} />
          <Route path="discover" element={<DiscoverBusinessesPage />} />
          <Route path="calling-queue" element={<CallingQueuePage />} />
        </Route>
        <Route path="projects" element={<WorkflowProjectsRouteGuard><PhotographyProjectsPage /></WorkflowProjectsRouteGuard>} />
        <Route path="projects/:projectId" element={<WorkflowProjectsRouteGuard><PhotographyProjectPage /></WorkflowProjectsRouteGuard>} />
        <Route path="workflows" element={<WorkflowProjectsRouteGuard><WorkflowOperationsPage /></WorkflowProjectsRouteGuard>} />
        <Route path="workflows/templates" element={<WorkflowRouteGuard><WorkflowsPage /></WorkflowRouteGuard>} />
        <Route path="workflows/reports" element={<WorkflowProjectsRouteGuard><WorkflowReportsPage /></WorkflowProjectsRouteGuard>} />
        <Route path="workflows/projects/:projectId" element={<WorkflowProjectsRouteGuard><PhotographyProjectPage /></WorkflowProjectsRouteGuard>} />
        <Route path="workflows/:workflowId" element={<WorkflowRouteGuard><WorkflowEditorPage /></WorkflowRouteGuard>} />
        <Route path="quotations" element={<AuthorizedScreen screen="Quotations"><QuotationPage /></AuthorizedScreen>} />
        <Route path="quotations/new" element={<AuthorizedScreen screen="New Quotation"><QuotationEditorPage /></AuthorizedScreen>} />
        <Route path="quotations/:quotationId" element={<AuthorizedScreen screen="Quotation"><QuotationEditorPage /></AuthorizedScreen>} />
        <Route path="commerce/storefront" element={<CommerceRouteGuard><StorefrontAdminPage /></CommerceRouteGuard>} />
        <Route path="commerce/catalogue" element={<CommerceRouteGuard><CatalogueAdminPage /></CommerceRouteGuard>} />
        <Route path="commerce/website-catalog" element={<CommerceRouteGuard><WebsiteCatalogImportPage /></CommerceRouteGuard>} />
        <Route path="commerce/reply-activity" element={<CatalogReplyActivityRouteGuard><CatalogReplyActivityPage /></CatalogReplyActivityRouteGuard>} />
        <Route path="commerce/catalogue/new" element={<CommerceRouteGuard><CatalogueItemEditorPage /></CommerceRouteGuard>} />
        <Route path="commerce/catalogue/:itemId/edit" element={<CommerceRouteGuard><CatalogueItemEditorPage /></CommerceRouteGuard>} />
        <Route path="commerce/catalogue/:itemId/pricing" element={<CommerceRouteGuard><BuyingPricingEditorPage /></CommerceRouteGuard>} />
        <Route path="commerce/categories" element={<CommerceRouteGuard><CategoriesAdminPage /></CommerceRouteGuard>} />
        <Route path="commerce/shipping" element={<CommerceRouteGuard><CommerceShippingSettingsPage /></CommerceRouteGuard>} />
        <Route path="commerce/settings" element={<CommerceRouteGuard><CommerceSettingsPage /></CommerceRouteGuard>} />
        <Route path="commerce/links" element={<CommerceTrackingRouteGuard><TrackingLinksPage /></CommerceTrackingRouteGuard>} />
        <Route path="commerce/orders" element={<CommerceRouteGuard><CommerceOrdersPage /></CommerceRouteGuard>} />
        <Route path="commerce/orders/:orderReference" element={<CommerceRouteGuard><CommerceOrderDetailPage /></CommerceRouteGuard>} />
        <Route path="commerce/*" element={<CommerceRouteGuard><CommerceAdminFoundation /></CommerceRouteGuard>} />
        <Route path="revenue" element={<RevenueRouteGuard><RevenuePage /></RevenueRouteGuard>} />
        <Route path="reports" element={<AuthorizedScreen screen="Reports"><ReportsPage /></AuthorizedScreen>} />
        <Route path="integrations" element={<AuthorizedScreen screen="Integrations"><IntegrationsPage /></AuthorizedScreen>} />
        <Route path="meta-setup" element={<AuthorizedScreen screen="Instagram/Facebook Setup" />} />
        <Route path="subscription" element={<AuthorizedScreen screen="Subscription">{billingWorkspace ? <BillingSubscriptionPage /> : <SubscriptionPage />}</AuthorizedScreen>} />
        <Route
          path="simple-billing"
          element={<SimpleBillingRouteGuard><SimpleBillingShell /></SimpleBillingRouteGuard>}
        >
          <Route index element={<SimpleBillingHomePage />} />
          <Route path="new" element={<NewBillPage />} />
          <Route path="estimates" element={<ComparativeEstimatesPage />} />
          <Route path="invoices" element={<PreviousBillsPage />} />
          <Route path="pending-payments" element={<PendingPaymentsPage />} />
          <Route path="collections" element={<CollectionsSummaryPage />} />
          <Route path="expenses" element={<ExpensesPage />} />
          <Route path="business-summary" element={<BusinessSummaryPage />} />
          <Route path="invoices/:invoiceId" element={<BillDetailsPage />} />
          <Route path="invoices/:invoiceId/edit" element={<NewBillPage />} />
          <Route path="products" element={<ProductsRatesPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="reports" element={<SalesSummaryPage />} />
          <Route path="settings" element={<BillingSettingsPage />} />
        </Route>
        <Route path="admin/workflow-launch" element={<AuthorizedScreen screen="Workflow Controlled Launch"><WorkflowLaunchPage /></AuthorizedScreen>} />
        <Route path="admin/billing-inventory" element={<AuthorizedScreen screen="Billing Inventory Access"><BillingInventoryAccessPage /></AuthorizedScreen>} />
        <Route path="admin" element={<AuthorizedScreen screen={user.role === "super_admin" ? "Clients" : billingWorkspace ? "Team" : "Team & Setup"}>{billingWorkspace ? <BillingTeamPage /> : <TeamSetupPage />}</AuthorizedScreen>} />
        <Route path="setup" element={<AuthorizedScreen screen="Workspace Setup"><WorkspaceSetupPage /></AuthorizedScreen>} />
        <Route path="*" element={<Navigate to={homePath} replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
